import * as React from 'react';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import {
  DialogContent,
  Dialog as MDialog,
  type DialogProps as MDialogProps,
} from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { IconButton } from '../IconButton';

type NeWDialogProps = Omit<MDialogProps, 'open'>;

export type DialogProps = NeWDialogProps & {
  dialogTitle: string;
  dialogAction?: string;
  cancelAction?: string;
  openDialog?: boolean;
  displayCloseButton?: boolean;
  onDialogAction?: () => void | Promise<void>; // the action which is confirmed
  closeAfterAction?: boolean;
  onDialogClose: () => void;
  children?: React.ReactNode;
  width?: string;
};

export const Dialog: React.FunctionComponent<DialogProps> = (
  props: DialogProps,
) => {
  const {
    openDialog = false,
    onDialogClose,
    onDialogAction,
    dialogTitle,
    dialogAction,
    cancelAction,
    displayCloseButton = false,
    width = '500px',
    closeAfterAction = true,
    sx,
    ...rest
  } = props;
  const [open, setOpen] = React.useState(openDialog);

  React.useEffect(() => {
    setOpen(openDialog);
  }, [openDialog]);

  const handleClose = (): void => {
    setOpen(false);
    onDialogClose();
  };

  const handleConfirm = (): void => {
    if (closeAfterAction) setOpen(false);
    if (onDialogAction !== undefined) void onDialogAction();
  };

  return (
    <div>
      <MDialog
        {...rest}
        sx={{
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: `${width}`,
            },
          },

          // remark:
          // theme.zIndex.drawer is usually set to 1200.
          // theme.zIndex.modal is usually set to 1300.
          zIndex: 1400, // Set z-index higher than drawer and modal
        }}
        open={open}
        scroll="paper"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent sx={{ padding: 0, ...sx }}>
          <DialogTitle
            id="alert-dialog-title"
            color="primary"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              fontSize: 18,
            }}
          >
            {dialogTitle}
            {(displayCloseButton ?? dialogTitle === '') ? (
              <IconButton
                size="small"
                icon={<CloseIcon color="primary" />}
                onClick={handleClose}
              />
            ) : (
              <div />
            )}
          </DialogTitle>
          {props.children}
          <div className="flex flex-row justify-center">
            {dialogAction !== undefined ? (
              <DialogActions
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Button onClick={handleConfirm} sx={{ textTransform: 'none' }}>
                  {dialogAction}
                </Button>
              </DialogActions>
            ) : (
              <div />
            )}
            {cancelAction !== undefined ? (
              <DialogActions
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
                  {cancelAction}
                </Button>
              </DialogActions>
            ) : (
              <div />
            )}
          </div>
        </DialogContent>
      </MDialog>
    </div>
  );
};
